import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { FaDownload } from "react-icons/fa";
import { apiUrl } from "../config";

const STOP_WORDS = new Set([
  "the", "and", "a", "an", "is", "was", "were", "of", "to", "in", "for", "on",
  "with", "at", "by", "from", "as", "that", "this", "it", "its", "be", "been",
  "are", "or", "but", "not", "no", "if", "then", "than", "so", "such", "my",
  "your", "their", "his", "her", "we", "you", "they", "them", "us", "had",
  "have", "has", "do", "does", "did", "will", "would", "could", "should",
  "may", "might", "can", "all", "very", "really", "just", "only", "also",
  "too", "more", "most", "some", "any", "every", "each", "much", "many",
  "when", "what", "who", "how", "why", "where", "there", "here", "into",
  "out", "about", "over", "again",
  "le", "la", "les", "un", "une", "des", "du", "de", "et", "ou", "mais",
  "donc", "pour", "par", "avec", "sans", "dans", "sur", "sous", "vers",
  "chez", "que", "qui", "quoi", "ce", "cette", "ces", "il", "elle", "ils",
  "elles", "je", "tu", "nous", "vous", "ne", "pas", "plus", "moins", "tres",
  "trop", "aussi", "alors", "encore", "deja", "etre", "est", "sont", "etait",
  "avoir", "fait", "faire", "ont", "leur", "leurs", "son", "ses", "mon",
  "mes", "ton", "tes", "votre", "vos", "notre", "nos",
]);

const buildWordFrequencies = (texts, maxWords = 40) => {
  const counts = new Map();
  for (const text of texts) {
    if (!text) continue;
    const normalized = text.toLowerCase().replace(/[^\p{L}\s'-]/gu, " ");
    for (const raw of normalized.split(/\s+/)) {
      const word = raw.replace(/^['-]+|['-]+$/g, "");
      if (word.length < 3) continue;
      if (STOP_WORDS.has(word)) continue;
      counts.set(word, (counts.get(word) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, maxWords);
};

const formatCsvDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const adminAuthHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
});

const displayName = (name) => {
  if (!name) return "—";
  if (typeof name === "string") return name;
  if (typeof name === "object") return name.en || name.fr || "—";
  return String(name);
};

const PRIOR_VISIT_LABELS = {
  within_week: "Within the last week",
  within_month: "Within the last month",
  within_year: "Within the last year",
  over_year: "Over a year ago",
  never: "Never visited before",
};

const RETURN_LIKELIHOOD_LABELS = {
  very_likely: "Very likely",
  somewhat_likely: "Somewhat likely",
  somewhat_unlikely: "Somewhat unlikely",
  very_unlikely: "Very unlikely",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRating = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(2)} / 5`;
};

const CoupalFeedbackCard = ({ responses, campaignNameById }) => {
  const entries = (responses || []).filter(
    (r) => r.coupalSuggestions && r.coupalSuggestions.trim().length > 0,
  );
  const hasFeedback = entries.length > 0;

  const words = hasFeedback
    ? buildWordFrequencies(entries.map((e) => e.coupalSuggestions))
    : [];

  const fontFor = (count, max, min) => {
    if (max === min) return 18;
    const t =
      (Math.log(count) - Math.log(min)) / (Math.log(max) - Math.log(min));
    return Math.round(12 + t * 18);
  };

  const handleDownload = () => {
    if (!hasFeedback) return;

    const groups = { 5: [], 4: [], 3: [], 2: [], 1: [], unrated: [] };
    for (const e of entries) {
      const key =
        e.coupalRating >= 1 && e.coupalRating <= 5 ? e.coupalRating : "unrated";
      groups[key].push(e);
    }

    const lines = [];
    lines.push("Coupal suggestions — Platform survey results");
    lines.push(`Downloaded: ${new Date().toLocaleString()}`);
    lines.push(`Total responses with suggestions: ${entries.length}`);
    lines.push("");

    const sep =
      "================================================================";
    const order = [1, 2, 3, 4, 5, "unrated"];
    for (const key of order) {
      const items = groups[key];
      if (!items.length) continue;
      const heading =
        key === "unrated"
          ? "NO COUPAL RATING"
          : `${key} STAR${key === 1 ? "" : "S"}`;
      lines.push(sep);
      lines.push(`${heading}  (n = ${items.length})`);
      lines.push(sep);
      lines.push("");
      for (const e of items) {
        const date = e.submittedAt ? formatCsvDate(e.submittedAt) : "";
        const campaign = e.campaignId
          ? campaignNameById?.get(e.campaignId) || e.campaignId
          : "";
        lines.push(`[${date}${campaign ? ` — ${campaign}` : ""}]`);
        lines.push(e.coupalSuggestions.trim());
        lines.push("");
      }
    }

    const content = lines.join("\r\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `coupal-suggestions-${formatCsvDate(
      new Date().toISOString(),
    )}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
        <Box>
          <Heading size="sm" color="gray.700">
            Feedback
          </Heading>
          <Text fontSize="sm" color="gray.600">
            {hasFeedback
              ? `${entries.length} ${
                  entries.length === 1 ? "response" : "responses"
                } with comments`
              : "Open-ended Coupal suggestions"}
          </Text>
        </Box>
        {hasFeedback && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FaDownload />}
            onClick={handleDownload}
          >
            Download
          </Button>
        )}
      </Flex>

      {!hasFeedback ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No Coupal suggestions submitted yet.
        </Text>
      ) : words.length === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          Suggestions received, but no common words to display yet.
        </Text>
      ) : (
        <Box
          bg="white"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          p={4}
          minH="180px"
        >
          {(() => {
            const max = words[0][1];
            const min = words[words.length - 1][1];
            return (
              <Flex wrap="wrap" gap={3} justify="center" align="baseline">
                {words.map(([word, count]) => {
                  const size = fontFor(count, max, min);
                  const ratio = max === min ? 1 : (count - min) / (max - min);
                  const color =
                    ratio > 0.66
                      ? "green.700"
                      : ratio > 0.33
                        ? "green.500"
                        : "green.400";
                  return (
                    <Text
                      key={word}
                      fontSize={`${size}px`}
                      fontWeight={ratio > 0.5 ? "bold" : "medium"}
                      color={color}
                      lineHeight="1.1"
                      title={`${word} — ${count} ${
                        count === 1 ? "mention" : "mentions"
                      }`}
                    >
                      {word}
                    </Text>
                  );
                })}
              </Flex>
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

const SurveyResultsAdmin = ({ campaigns = [], organizations = [] }) => {
  const toast = useToast();
  const [campaignId, setCampaignId] = useState("");
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [sortField, setSortField] = useState("submittedAt");
  const [sortDir, setSortDir] = useState("desc");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortArrow = (field) =>
    sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const orgNameById = useMemo(() => {
    const map = new Map();
    organizations.forEach((o) => map.set(o.id, displayName(o.name)));
    return map;
  }, [organizations]);

  const campaignOptions = useMemo(() => {
    return [...campaigns]
      .map((c) => ({
        id: c.id,
        label: `${displayName(c.name)}${
          c.organizationId
            ? ` — ${orgNameById.get(c.organizationId) || "Unknown org"}`
            : ""
        }`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [campaigns, orgNameById]);

  const campaignNameById = useMemo(() => {
    const map = new Map();
    campaigns.forEach((c) => map.set(c.id, displayName(c.name)));
    return map;
  }, [campaigns]);

  const coupalRatingCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    responses.forEach((r) => {
      const rounded = Math.round(Number(r.coupalRating));
      if (rounded >= 1 && rounded <= 5) counts[rounded] += 1;
    });
    return counts;
  }, [responses]);

  const coupalRatingTotal = useMemo(
    () => [1, 2, 3, 4, 5].reduce((s, k) => s + coupalRatingCounts[k], 0),
    [coupalRatingCounts],
  );

  const sortedResponses = useMemo(() => {
    const list = [...responses];
    const dir = sortDir === "asc" ? 1 : -1;
    const valueFor = (r) => {
      switch (sortField) {
        case "submittedAt":
          return r.submittedAt ? new Date(r.submittedAt).getTime() : 0;
        case "campaign":
          return (
            campaignNameById.get(r.campaignId) ||
            r.campaignId ||
            ""
          )
            .toString()
            .toLowerCase();
        case "coupalRating":
          return r.coupalRating ?? -1;
        case "coupalSuggestions":
          return (r.coupalSuggestions || "").toString().toLowerCase();
        default:
          return 0;
      }
    };
    list.sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [responses, sortField, sortDir, campaignNameById]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = campaignId ? { campaignId } : {};
        const res = await axios.get(`${apiUrl}/admin/surveys/responses`, {
          headers: adminAuthHeader(),
          params,
        });
        setResponses(res.data.responses || []);
        setStats(res.data.stats || null);
      } catch (err) {
        console.error("Failed to load survey responses", err);
        toast({
          title: "Error",
          description: "Failed to load survey responses",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [campaignId, toast]);

  const openResponse = (resp) => {
    setSelectedResponse(resp);
    onOpen();
  };

  return (
    <Box>
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={4}
        align={{ base: "stretch", md: "flex-end" }}
        mb={6}
      >
        <Box flex="1" maxW="500px">
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Filter by campaign
          </Text>
          <Select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            placeholder="All campaigns"
          >
            {campaignOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Box>
      </Stack>

      {loading ? (
        <HStack justify="center" py={10}>
          <Spinner />
          <Text>Loading survey results…</Text>
        </HStack>
      ) : (
        <>
          <Box
            bg="gray.50"
            borderRadius="lg"
            p={{ base: 4, md: 5 }}
            mb={6}
            border="1px solid"
            borderColor="gray.200"
          >
            <Heading size="sm" mb={4} color="gray.700">
              Aggregate
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Stat>
                <StatLabel>Total responses</StatLabel>
                <StatNumber>{stats?.count || 0}</StatNumber>
                <StatHelpText>
                  {campaignId
                    ? campaignNameById.get(campaignId) || "Selected campaign"
                    : "All campaigns"}
                </StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Avg. business service rating</StatLabel>
                <StatNumber>{formatRating(stats?.avgServiceRating)}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Avg. Coupal rating</StatLabel>
                <StatNumber>{formatRating(stats?.avgCoupalRating)}</StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <Box
              bg="gray.50"
              borderRadius="lg"
              p={{ base: 4, md: 5 }}
              border="1px solid"
              borderColor="gray.200"
            >
              <Heading size="sm" color="gray.700" mb={3}>
                Coupal rating distribution
              </Heading>

            {coupalRatingTotal === 0 ? (
              <Text color="gray.500" fontSize="sm" py={2}>
                No survey responses yet.
              </Text>
            ) : (
              <>
                <Flex align="baseline" gap={3} mb={1} flexWrap="wrap">
                  <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
                    {stats?.avgCoupalRating != null
                      ? Number(stats.avgCoupalRating).toFixed(1)
                      : "—"}
                  </Text>
                  <Text fontSize="2xl" color="yellow.500" lineHeight="1">
                    ★
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Average rating
                  </Text>
                </Flex>
                <Text fontSize="xs" color="gray.500" mb={4}>
                  Based on {coupalRatingTotal}{" "}
                  {coupalRatingTotal === 1 ? "response" : "responses"}
                </Text>

                <VStack spacing={2} align="stretch">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const n = coupalRatingCounts[rating] || 0;
                    const pct = coupalRatingTotal
                      ? (n / coupalRatingTotal) * 100
                      : 0;
                    return (
                      <HStack key={rating} spacing={3} fontSize="sm">
                        <HStack spacing={1} minW="40px" justify="flex-end">
                          <Text color="gray.700">{rating}</Text>
                          <Text color="yellow.500" lineHeight="1">
                            ★
                          </Text>
                        </HStack>
                        <Box
                          flex="1"
                          bg="gray.200"
                          height="10px"
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            bg="green.500"
                            height="100%"
                            width={`${pct}%`}
                            borderRadius="full"
                          />
                        </Box>
                        <Text color="gray.600" minW="70px" textAlign="right">
                          {n} ({Math.round(pct)}%)
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              </>
            )}
            </Box>

            <CoupalFeedbackCard
              responses={responses}
              campaignNameById={campaignNameById}
            />
          </SimpleGrid>

          <Heading size="sm" mb={3} color="gray.700">
            Individual responses ({responses.length})
          </Heading>

          {responses.length === 0 ? (
            <Text color="gray.500" py={4}>
              No survey responses yet.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th
                      cursor="pointer"
                      userSelect="none"
                      onClick={() => toggleSort("submittedAt")}
                    >
                      Submitted{sortArrow("submittedAt")}
                    </Th>
                    {!campaignId && (
                      <Th
                        textAlign="center"
                        cursor="pointer"
                        userSelect="none"
                        onClick={() => toggleSort("campaign")}
                      >
                        Campaign{sortArrow("campaign")}
                      </Th>
                    )}
                    <Th
                      textAlign="center"
                      cursor="pointer"
                      userSelect="none"
                      onClick={() => toggleSort("coupalRating")}
                    >
                      Coupal rating{sortArrow("coupalRating")}
                    </Th>
                    <Th
                      cursor="pointer"
                      userSelect="none"
                      onClick={() => toggleSort("coupalSuggestions")}
                    >
                      Coupal Suggestions{sortArrow("coupalSuggestions")}
                    </Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedResponses.map((r) => (
                    <Tr key={r.id} _hover={{ bg: "gray.50" }}>
                      <Td>{formatDateTime(r.submittedAt)}</Td>
                      {!campaignId && (
                        <Td textAlign="center">
                          {r.campaignId
                            ? campaignNameById.get(r.campaignId) || r.campaignId
                            : "—"}
                        </Td>
                      )}
                      <Td textAlign="center">
                        {r.coupalRating
                          ? "★".repeat(r.coupalRating) +
                            "☆".repeat(Math.max(0, 5 - r.coupalRating))
                          : "—"}
                      </Td>
                      <Td>
                        {r.coupalSuggestions ? (
                          <Text noOfLines={2} maxW="320px">
                            {r.coupalSuggestions}
                          </Text>
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => openResponse(r)}
                        >
                          View
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Survey response</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedResponse && (
              <VStack align="stretch" spacing={3} fontSize="sm">
                <HStack justify="space-between">
                  <Text color="gray.500">Submitted</Text>
                  <Text>{formatDateTime(selectedResponse.submittedAt)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Business</Text>
                  <Text>{displayName(selectedResponse.businessName)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Campaign</Text>
                  <Text>
                    {selectedResponse.campaignId
                      ? campaignNameById.get(selectedResponse.campaignId) ||
                        selectedResponse.campaignId
                      : "—"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Postal code</Text>
                  <Text>{selectedResponse.postalCode || "—"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Redemption date</Text>
                  <Text>{formatDateTime(selectedResponse.redemptionDate)}</Text>
                </HStack>
                <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                  <Text color="gray.500" mb={1}>
                    Prior visit
                  </Text>
                  <Text>
                    {PRIOR_VISIT_LABELS[selectedResponse.priorVisit] ||
                      selectedResponse.priorVisit ||
                      "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" mb={1}>
                    Service rating
                  </Text>
                  <Text>
                    {selectedResponse.serviceRating
                      ? `${selectedResponse.serviceRating} / 5`
                      : "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" mb={1}>
                    Return likelihood
                  </Text>
                  <Text>
                    {RETURN_LIKELIHOOD_LABELS[
                      selectedResponse.returnLikelihood
                    ] ||
                      selectedResponse.returnLikelihood ||
                      "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" mb={1}>
                    Feedback for business
                  </Text>
                  <Text whiteSpace="pre-wrap">
                    {selectedResponse.businessFeedback || (
                      <Text as="span" color="gray.400">
                        (none)
                      </Text>
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" mb={1}>
                    Coupal rating
                  </Text>
                  <Text>
                    {selectedResponse.coupalRating
                      ? `${selectedResponse.coupalRating} / 5`
                      : "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" mb={1}>
                    Coupal suggestions
                  </Text>
                  <Text whiteSpace="pre-wrap">
                    {selectedResponse.coupalSuggestions || (
                      <Text as="span" color="gray.400">
                        (none)
                      </Text>
                    )}
                  </Text>
                </Box>
                <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                  <Text color="gray.500" fontSize="xs" mb={1}>
                    Response ID
                  </Text>
                  <Text fontFamily="mono" fontSize="xs">
                    {selectedResponse.id}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SurveyResultsAdmin;
